import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import API from '../api';

const STEPS = ['account', 'questionnaire'];

const contentTypes = [
  { id: 'gaming',    label: 'Gaming' },
  { id: 'fitness',   label: 'Fitness & gym' },
  { id: 'irl',       label: 'IRL / Just chatting' },
  { id: 'music',     label: 'Music' },
  { id: 'creative',  label: 'Art & creative' },
  { id: 'sports',    label: 'Sports' },
  { id: 'comedy',    label: 'Comedy' },
  { id: 'other',     label: 'Other' },
];

const goalOptions = [
  { id: 'grow_youtube',   label: 'Grow my YouTube channel' },
  { id: 'grow_following', label: 'Build a bigger following' },
  { id: 'save_time',      label: 'Save time on editing & clipping' },
  { id: 'monetise',       label: 'Monetise my content' },
  { id: 'brand_deals',    label: 'Attract brand deals' },
  { id: 'go_full_time',   label: 'Go full time as a creator' },
];

const frequencyOptions = [
  { id: 'daily',     label: 'Daily' },
  { id: '3-4x',      label: '3–4 times a week' },
  { id: '1-2x',      label: '1–2 times a week' },
  { id: 'occasional',label: 'Occasionally' },
];

const audienceOptions = [
  { id: 'starting', label: 'Just starting out' },
  { id: 'small',    label: 'Under 1,000 followers' },
  { id: 'medium',   label: '1,000 – 10,000' },
  { id: 'large',    label: '10,000 – 100,000' },
  { id: 'xlarge',   label: '100,000+' },
];

function Chip({ label, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '8px 16px', borderRadius: 20, fontSize: 13, fontWeight: 500,
        cursor: 'pointer', fontFamily: 'var(--font-body)', transition: 'all 0.15s',
        background: selected ? 'var(--accent)' : 'var(--surface2)',
        color: selected ? '#000' : 'var(--text-dim)',
        border: selected ? '1px solid var(--accent)' : '1px solid var(--border)',
      }}
    >
      {label}
    </button>
  );
}

export default function Signup() {
  const [step, setStep] = useState('account');
  const [form, setForm] = useState({ email:'', password:'', firstName:'', lastName:'', marketingOptIn: true });
  const [quiz, setQuiz] = useState({ contentTypes:[], goals:[], frequency:'', audienceSize:'', platforms:['youtube'] });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const set = k => e => setForm(f => ({...f, [k]: e.target.value}));
  const toggle = k => () => setForm(f => ({...f, [k]: !f[k]}));
  const toggleGoal = id => setQuiz(q => ({
    ...q, goals: q.goals.includes(id) ? q.goals.filter(g=>g!==id) : [...q.goals, id]
  }));
  const toggleContentType = id => setQuiz(q => ({
    ...q, contentTypes: q.contentTypes.includes(id) ? q.contentTypes.filter(c=>c!==id) : [...q.contentTypes, id]
  }));

  const handleAccount = async e => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const displayName = form.lastName ? `${form.firstName} ${form.lastName}` : form.firstName;
      await signup(form.email, form.password, '', displayName, form.firstName, form.lastName, form.marketingOptIn);
      setStep('questionnaire');
    } catch (err) {
      setError(err.response?.data?.error || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionnaire = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await API.post('/api/auth/questionnaire', {
        content_type: quiz.contentTypes,
        goals: quiz.goals,
        posting_frequency: quiz.frequency,
        audience_size: quiz.audienceSize,
        platforms: quiz.platforms,
      });
      navigate('/billing');
    } catch (err) {
      // Still proceed even if questionnaire fails
      navigate('/billing');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width:'100%', padding:'11px 14px', background:'var(--surface2)',
    border:'1px solid var(--border)', borderRadius:8, color:'var(--text)',
    fontFamily:'var(--font-body)', fontSize:14, outline:'none',
  };

  const fieldStyle = { display:'flex', flexDirection:'column', gap:6 };
  const labelStyle = { fontSize:12, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)' };

  if (step === 'account') return (
    <div className="auth-page">
      <div className="auth-box">
        <div className="auth-logo">Clump</div>
        <div className="auth-tagline">Your clips, automated.</div>
        <div className="auth-title" style={{marginBottom:24}}>Create account</div>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={handleAccount} style={{display:'flex', flexDirection:'column', gap:14}}>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
            <div style={fieldStyle}>
              <label style={labelStyle}>First name <span style={{color:'var(--accent)'}}>*</span></label>
              <input value={form.firstName} onChange={set('firstName')} required placeholder="Zoe" style={inputStyle}/>
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Last name <span style={{color:'var(--text-muted)', fontWeight:400}}>(optional)</span></label>
              <input value={form.lastName} onChange={set('lastName')} placeholder="Collins" style={inputStyle}/>
            </div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Email <span style={{color:'var(--accent)'}}>*</span></label>
            <input type="email" value={form.email} onChange={set('email')} required placeholder="you@example.com" style={inputStyle}/>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Password <span style={{color:'var(--accent)'}}>*</span></label>
            <input type="password" value={form.password} onChange={set('password')} required placeholder="Min. 8 characters" minLength={8} style={inputStyle}/>
          </div>
          <div style={{display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer'}} onClick={toggle('marketingOptIn')}>
            <div style={{
              width:18, height:18, borderRadius:4, flexShrink:0, marginTop:1,
              background: form.marketingOptIn ? 'var(--accent)' : 'var(--surface2)',
              border: `1px solid ${form.marketingOptIn ? 'var(--accent)' : 'var(--border)'}`,
              display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s',
            }}>
              {form.marketingOptIn && (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
            </div>
            <div style={{fontSize:12, color:'var(--text-muted)', lineHeight:1.5, userSelect:'none'}}>
              Send me tips, updates and early access news from Clump
            </div>
          </div>
          <button className="btn btn-primary" type="submit" disabled={loading} style={{marginTop:4}}>
            {loading ? 'Creating account...' : 'Continue →'}
          </button>
        </form>
        <div className="auth-link" style={{marginTop:20}}>
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );

  if (step === 'questionnaire') return (
    <div className="auth-page" style={{alignItems:'flex-start', paddingTop:60}}>
      <div className="auth-box" style={{maxWidth:560}}>
        <div className="auth-logo">Clump</div>
        <div style={{fontSize:22, fontFamily:'var(--font-display)', fontWeight:800, letterSpacing:'-0.5px', marginBottom:4}}>
          Tell us about yourself
        </div>
        <div style={{color:'var(--text-muted)', fontSize:13, marginBottom:28}}>
          This helps us personalise your insights and scheduling from day one.
        </div>

        <form onSubmit={handleQuestionnaire} style={{display:'flex', flexDirection:'column', gap:24}}>

          {/* Content type */}
          <div>
<div style={{fontSize:13, fontWeight:600, marginBottom:10}}>What kind of content do you stream? <span style={{color:'var(--text-muted)', fontWeight:400}}>(pick all that apply)</span></div>
            <div style={{display:'flex', flexWrap:'wrap', gap:8}}>
              {contentTypes.map(c => (
                <Chip key={c.id} label={c.label} selected={quiz.contentTypes.includes(c.id)}
                  onClick={() => toggleContentType(c.id)} />
              ))}
            </div>
          </div>

          {/* Goals */}
          <div>
            <div style={{fontSize:13, fontWeight:600, marginBottom:10}}>What are your goals? <span style={{color:'var(--text-muted)', fontWeight:400}}>(pick all that apply)</span></div>
            <div style={{display:'flex', flexWrap:'wrap', gap:8}}>
              {goalOptions.map(g => (
                <Chip key={g.id} label={g.label} selected={quiz.goals.includes(g.id)}
                  onClick={() => toggleGoal(g.id)} />
              ))}
            </div>
          </div>

          {/* Stream frequency */}
          <div>
            <div style={{fontSize:13, fontWeight:600, marginBottom:10}}>How often do you stream?</div>
            <div style={{display:'flex', flexWrap:'wrap', gap:8}}>
              {frequencyOptions.map(f => (
                <Chip key={f.id} label={f.label} selected={quiz.frequency===f.id}
                  onClick={() => setQuiz(q=>({...q, frequency:f.id}))} />
              ))}
            </div>
          </div>

          {/* Audience size */}
          <div>
            <div style={{fontSize:13, fontWeight:600, marginBottom:10}}>How big is your current following?</div>
            <div style={{display:'flex', flexWrap:'wrap', gap:8}}>
              {audienceOptions.map(a => (
                <Chip key={a.id} label={a.label} selected={quiz.audienceSize===a.id}
                  onClick={() => setQuiz(q=>({...q, audienceSize:a.id}))} />
              ))}
            </div>
          </div>

          <div style={{display:'flex', gap:12, marginTop:4}}>
            <button className="btn btn-primary" type="submit" disabled={loading} style={{flex:1}}>
              {loading ? 'Saving...' : 'Get started →'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => navigate('/billing')} style={{flex:'none'}}>
              Skip
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
